import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { Files } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { Loading, Empty, Panel, WorkflowBadge } from "./_shared";

export default function DocumentsGuide() {
  const [data, setData] = useState({ items: [], count: 0, total: 0, vocabulary: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    employee_id: "", doc_type: "", source: "", department: "",
    status: "", search: "", include_archived: true,
  });
  const [view, setView] = useState("flat");
  const setF = (k, v) => setFilters((s) => ({ ...s, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined && !(typeof v === "boolean" && v === true && k === "include_archived"))
          params.set(k, String(v));
        if (k === "include_archived" && !v) params.set(k, "false");
      });
      const { data: d } = await api.get(`/hr/documents/register?${params.toString()}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filters]);

  const grouped = useMemo(() => {
    if (view === "byEmployee") {
      const g = {};
      data.items.forEach((d) => {
        const k = d.employee_id || "unassigned";
        (g[k] = g[k] || { label: d.employee_name || "—", code: d.employee_code, items: [] }).items.push(d);
      });
      return g;
    }
    if (view === "byType") {
      const g = {};
      data.items.forEach((d) => {
        (g[d.doc_type] = g[d.doc_type] || { items: [] }).items.push(d);
      });
      return g;
    }
    return null;
  }, [data.items, view]);

  const v = data.vocabulary || {};

  return (
    <div className="space-y-4" data-testid="hr-documents-register">
      <Panel
        title="HR Document Register · live"
        icon={<Files size={16} style={{ color: "#1E5CBA" }} />}
      >
        <div className="px-5 py-3 text-xs text-slate-600">
          Live aggregator across <b>Staff Directory, Contracts, Salary, Payroll, Performance,
          Warnings, Defenses, Leaves, Separations</b> and <b>Past Employees</b>. Click any row to
          open the source record, audit trail, or download the document.
        </div>
      </Panel>

      <div className="dc-card p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[160px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Search</div>
          <Input value={filters.search} onChange={(e) => setF("search", e.target.value)}
            placeholder="employee, code, type, title…" data-testid="docs-search" />
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Document type</div>
          <Select value={filters.doc_type || "_any"}
            onValueChange={(x) => setF("doc_type", x === "_any" ? "" : x)}>
            <SelectTrigger data-testid="docs-doctype"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any type</SelectItem>
              {(v.doc_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Source</div>
          <Select value={filters.source || "_any"}
            onValueChange={(x) => setF("source", x === "_any" ? "" : x)}>
            <SelectTrigger data-testid="docs-source"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any source</SelectItem>
              {(v.sources || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Department</div>
          <Select value={filters.department || "_any"}
            onValueChange={(x) => setF("department", x === "_any" ? "" : x)}>
            <SelectTrigger data-testid="docs-department"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">Any department</SelectItem>
              {(v.departments || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Active vs archived</div>
          <Select value={filters.include_archived ? "all" : "active"}
            onValueChange={(x) => setF("include_archived", x === "all")}>
            <SelectTrigger data-testid="docs-archived"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="all">All (incl. past employees)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Group by</div>
          <Select value={view} onValueChange={setView}>
            <SelectTrigger data-testid="docs-group"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="flat">Flat list</SelectItem>
              <SelectItem value="byEmployee">By employee</SelectItem>
              <SelectItem value="byType">By document type</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? <Loading /> : data.items.length === 0 ? (
        <Empty text="No documents match these filters." />
      ) : view === "flat" ? (
        <div className="dc-card overflow-hidden">
          <div className="px-5 py-2 text-[11px] font-mono-alt uppercase tracking-widest text-slate-500 border-b border-slate-100">
            {data.count.toLocaleString()} document(s) · pulled live from HR workflows
          </div>
          <table className="hidden w-full md:table">
            <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3">Document</th>
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3">Generated</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Open</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((d) => <DocRow key={d.id} d={d} />)}
            </tbody>
          </table>
          <ul className="divide-y divide-slate-100 md:hidden">
            {data.items.map((d) => <DocCard key={d.id} d={d} />)}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped || {}).map(([gk, g]) => (
            <div key={gk} className="dc-card overflow-hidden" data-testid={`docs-group-${gk}`}>
              <div className="flex items-center justify-between bg-slate-50 px-5 py-2.5 border-b border-slate-100">
                <div className="font-display text-sm font-semibold text-slate-900">
                  {view === "byEmployee"
                    ? `${g.label}${g.code ? ` · ${g.code}` : ""}`
                    : gk}
                </div>
                <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
                  {g.items.length} doc(s)
                </span>
              </div>
              <table className="hidden w-full md:table">
                <tbody className="divide-y divide-slate-100">
                  {g.items.map((d) => <DocRow key={d.id} d={d} grouped />)}
                </tbody>
              </table>
              <ul className="divide-y divide-slate-100 md:hidden">
                {g.items.map((d) => <DocCard key={d.id} d={d} />)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocRow({ d, grouped }) {
  const open = () => { if (d.download_url) window.open(d.download_url, "_blank"); };
  return (
    <tr className="text-sm hover:bg-slate-50 transition cursor-pointer"
        onClick={open} data-testid={`doc-row-${d.id}`}>
      <td className="px-5 py-3">
        <div className="font-semibold text-slate-900">{d.doc_type}</div>
        <div className="text-[11px] text-slate-500">{d.title}</div>
      </td>
      <td className="px-5 py-3 text-xs">
        {grouped ? <span className="text-slate-400">—</span> : (
          <>
            <div className="font-semibold text-slate-900">{d.employee_name || "—"}</div>
            <div className="text-[11px] text-slate-500">
              {d.employee_code || "—"}{d.department ? ` · ${d.department}` : ""}
              {d.is_archived && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-mono-alt uppercase tracking-wider text-amber-800">past</span>}
            </div>
          </>
        )}
      </td>
      <td className="px-5 py-3 text-xs">
        <span className="rounded bg-slate-100 px-2 py-0.5 font-mono-alt uppercase tracking-wider text-slate-700">{d.source}</span>
      </td>
      <td className="px-5 py-3 font-mono-alt text-xs">{d.generated_at ? formatDate(d.generated_at) : "—"}</td>
      <td className="px-5 py-3"><WorkflowBadge status={d.status} /></td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex items-center gap-1.5">
          {d.audit_entity && (
            <AuditTrailDrawer entity={d.audit_entity} entityId={d.ref_id} label="Audit" />
          )}
          {d.download_url ? (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); window.open(d.download_url, "_blank"); }}
              data-testid={`doc-download-${d.id}`}>
              Download
            </Button>
          ) : d.link_record_url ? (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); }}
              data-testid={`doc-open-${d.id}`}>
              Open record
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function DocCard({ d }) {
  return (
    <li className="p-4 text-sm hover:bg-slate-50 transition" data-testid={`doc-mrow-${d.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">{d.doc_type}</div>
          <div className="text-[11px] text-slate-500 truncate">{d.title}</div>
          <div className="mt-1 text-[11px] text-slate-500">
            {d.employee_name || "—"} · {d.employee_code || "—"}
            {d.is_archived && <span className="ml-1.5 rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-mono-alt uppercase tracking-wider text-amber-800">past</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <WorkflowBadge status={d.status} />
          <div className="mt-1 text-[10px] font-mono-alt uppercase tracking-wider text-slate-500">{d.source}</div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <div className="font-mono-alt text-[11px] text-slate-500">{d.generated_at ? formatDate(d.generated_at) : "—"}</div>
        <div className="inline-flex items-center gap-1.5">
          {d.audit_entity && (
            <AuditTrailDrawer entity={d.audit_entity} entityId={d.ref_id} label="Audit" />
          )}
          {d.download_url && (
            <Button size="sm" variant="outline" onClick={() => window.open(d.download_url, "_blank")}>
              Download
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
